import { useCallback, useMemo, useState } from 'react';
import { toast } from '@/components';

interface UseDeletedItemsOptions<T> {
    items: T[];
    getItemId: (item: T) => string;
    deleteItemsFn: (ids: string[]) => Promise<unknown>;
    successMessage?: string;
    errorMessage?: string;
}

export function useDeletedItems<T>({
    items,
    getItemId,
    deleteItemsFn,
    successMessage,
    errorMessage,
}: UseDeletedItemsOptions<T>) {
    const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

    const handleDelete = useCallback(
        async (itemId: string) => {
            try {
                await deleteItemsFn([itemId]);
                setDeletedIds((prev) => {
                    const newSet = new Set(prev);
                    newSet.add(itemId);
                    return newSet;
                });
                if (successMessage) {
                    toast.success(successMessage);
                }
            } catch {
                if (errorMessage) {
                    toast.error(errorMessage);
                }
            }
        },
        [deleteItemsFn, successMessage, errorMessage],
    );

    const filteredItems = useMemo(
        () => items.filter((item) => !deletedIds.has(getItemId(item))),
        [items, deletedIds],
    );

    const deletedCount = deletedIds.size;

    return {
        filteredItems,
        deletedCount,
        handleDelete,
    };
}
